import React, { useState } from 'react';

function Quiz({ questions, onComplete, levelId }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const hasAnswered = selectedAnswers[currentQuestion] !== undefined;

  const handleAnswer = (answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestion]: answerIndex
    });
  };

  const handleNext = () => {
    if (isLastQuestion) {
      calculateScore();
    } else {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((q, index) => {
      if (selectedAnswers[index] === q.correctAnswer) {
        correct++;
      }
    });
    setScore(correct);
    setShowResults(true);
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setScore(0);
  };

  const handleContinue = () => {
    const passed = (score / questions.length) >= 0.7; // 70% to pass
    onComplete(passed, score, questions.length);
  };

  if (showResults) {
    const percentage = Math.round((score / questions.length) * 100);
    const passed = percentage >= 70;

    return (
      <div className="bg-slate-800 rounded-lg p-8">
        <div className="text-center mb-8">
          <div className={`inline-block p-6 rounded-full mb-4 ${passed ? 'bg-green-600' : 'bg-yellow-600'}`}>
            <span className="text-6xl">{passed ? '🎉' : '📚'}</span>
          </div>
          <h2 className="text-3xl font-bold mb-2">
            {passed ? 'Great Job!' : 'Keep Learning!'}
          </h2>
          <p className="text-xl text-slate-300 mb-4">
            You scored {score} out of {questions.length} ({percentage}%)
          </p>
          <p className="text-slate-400">
            {passed
              ? 'You passed! You have completed this level.'
              : 'You need 70% to pass. Review the learning material and try again.'}
          </p>
        </div>

        {/* Answer Review */}
        <div className="space-y-4 mb-8">
          <h3 className="text-xl font-semibold mb-4">Review Your Answers</h3>
          {questions.map((q, index) => {
            const userAnswer = selectedAnswers[index];
            const correct = userAnswer === q.correctAnswer;

            return (
              <div key={index} className={`p-4 rounded-lg border-2 ${
                correct ? 'border-green-500 bg-green-900/20' : 'border-red-500 bg-red-900/20'
              }`}>
                <p className="font-semibold mb-2">
                  {index + 1}. {q.question}
                </p>
                <div className="space-y-2 text-sm">
                  <p className={correct ? 'text-green-400' : 'text-red-400'}>
                    Your answer: {q.options[userAnswer]}
                  </p>
                  {!correct && (
                    <p className="text-green-400">
                      Correct answer: {q.options[q.correctAnswer]}
                    </p>
                  )}
                  {q.explanation && (
                    <p className="text-slate-300 mt-2 italic">
                      💡 {q.explanation}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center">
          {!passed && (
            <button
              onClick={handleRetry}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-lg font-semibold"
            >
              Retry Quiz
            </button>
          )}
          <button
            onClick={() => handleContinue()}
            className={`px-8 py-3 rounded-lg font-semibold ${
              passed
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-slate-600 hover:bg-slate-700'
            }`}
          >
            {passed ? 'Continue →' : 'Back to Learning'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg p-8">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-slate-400">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm text-slate-400">
            {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-8">
        <h3 className="text-2xl font-bold mb-6">{question.question}</h3>

        {question.type === 'code' && question.code && (
          <div className="bg-slate-900 p-4 rounded-lg mb-6 border border-slate-700">
            <pre className="text-sm text-slate-200 overflow-x-auto">
              <code>{question.code}</code>
            </pre>
          </div>
        )}

        {/* Answer Options */}
        <div className="space-y-3">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswers[currentQuestion] === index;

            return (
              <button
                key={index}
                onClick={() => handleAnswer(index)}
                className={`w-full text-left p-4 rounded-lg border-2 transition ${
                  isSelected
                    ? 'border-blue-500 bg-blue-900/30'
                    : 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
                }`}
              >
                <span className="font-semibold mr-3 text-blue-400">
                  {String.fromCharCode(65 + index)}.
                </span>
                <span className={isSelected ? 'text-white' : 'text-slate-200'}>
                  {option}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className={`px-6 py-3 rounded-lg font-semibold ${
            currentQuestion === 0
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-slate-600 hover:bg-slate-500 text-white'
          }`}
        >
          ← Previous
        </button>

        <div className="text-sm text-slate-400">
          {Object.keys(selectedAnswers).length} of {questions.length} answered
        </div>

        <button
          onClick={handleNext}
          disabled={!hasAnswered}
          className={`px-6 py-3 rounded-lg font-semibold ${
            !hasAnswered
              ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isLastQuestion ? 'Submit Quiz →' : 'Next →'}
        </button>
      </div>
    </div>
  );
}

export default Quiz;
